import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  alpha,
} from "@mui/material";

// Icons
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import EmailIcon from "@mui/icons-material/Email";

// Components
import PageContainer from "../layouts/PageContainer";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppCircularProgress from "../components/common/AppCircularProgress";
import UpdateBranchSettingsModal from "../components/settings/UpdateBranchSettingsModal";
import AppButton from "../components/buttons/AppButton";
import AppEmailModal from "../components/common/AppEmailModal";
import UserDefaultsModal from "../components/settings/UserDefaultsModal";

// Store & Hooks
import { useAppDispatch, useAppSelector } from "../redux/store";
import { usePermissions } from "../hooks/usePermissions";
import { DocType } from "../shared/enums/DocType";

// Actions
import {
  getBranchSettings,
  clearAppCache,
  loadLocalSettings,
  resetSettingsServerStatus,
} from "../redux/actions/settingsActions";
import { getCurrencies } from "../redux/actions/currencyActions";
import { getPaymentMethods } from "../redux/actions/paymentMethodActions";
import { getCustomers } from "../redux/actions/customerActions";
import { emailBranchDetails } from "../redux/actions/branchActions";

const hardwareTypes = [
  {
    key: "thermal" as const,
    label: "Thermal Printer (POS)",
    icon: <ReceiptIcon color="success" />,
  },
  {
    key: "a4" as const,
    label: "A4/Standard Printer",
    icon: <PrintIcon color="info" />,
  },
];

const documentTypes = [
  { key: DocType.RECEIPT, label: "POS Receipts", hardware: "Thermal" },
  {
    key: DocType.CUSTOMER_ORDER,
    label: "Customer Orders",
    hardware: "Thermal",
  },
  { key: DocType.INVOICE, label: "Invoices", hardware: "A4" },
  { key: DocType.QUOTATION, label: "Quotations", hardware: "A4" },
  { key: DocType.PURCHASE_ORDER, label: "Purchase Orders", hardware: "A4" },
  { key: DocType.CREDIT_NOTE, label: "Credit Notes", hardware: "A4" },
];

const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const { userInfo } = useAppSelector((state) => state.auth);
  const {
    branchSettings,
    loadingBranchSettings,
    loadingUpdateBranchSettings,
    errorUpdateBranchSettings,
    serverStatus,
  } = useAppSelector((state) => state.settings);

  const { loadingEmail } = useAppSelector((state) => state.branch);

  // --- Local State ---
  const [clearCacheOpen, setClearCacheOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [defaultsModalOpen, setDefaultsModalOpen] = useState(false);

  // Print Modals State
  const [hwModal, setHwModal] = useState<{
    open: boolean;
    type: "thermal" | "a4";
    title: string;
  }>({
    open: false,
    type: "thermal",
    title: "",
  });

  const [docModal, setDocModal] = useState<{
    open: boolean;
    docType: DocType;
    title: string;
  }>({
    open: false,
    docType: DocType.RECEIPT,
    title: "",
  });

  const canUpdate = hasPermission("branch_setting:update");

  useEffect(() => {
    dispatch(getBranchSettings());
    dispatch(getCurrencies());
    dispatch(getPaymentMethods({ size: 10 }));
    dispatch(getCustomers({ size: 20 }));
    dispatch(loadLocalSettings());
  }, [dispatch]);

  useEffect(() => {
    if (
      serverStatus?.operation === "update-branch-settings" &&
      serverStatus?.status === 200
    ) {
      dispatch(resetSettingsServerStatus());
      setBranchModalOpen(false);
    }
  }, [serverStatus, dispatch]);

  const handleEmailSubmit = async (email: string): Promise<void> => {
    const success = await dispatch(emailBranchDetails({ email }));
    if (success) {
      setEmailModalOpen(false);
    }
  };

  const handleClearCacheConfirm = (): void => {
    dispatch(clearAppCache());
  };

  return (
    <PageContainer
      title="Settings & Preferences"
      subtitle="Manage your application preferences, ERP defaults, and printing configurations."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Settings", to: undefined },
      ]}
    >
      <Stack spacing={4} sx={{ maxWidth: 800, mx: "auto", pb: 4 }}>
        {/* 1. ACCOUNT PROFILE */}
        <Box>
          <Typography
            variant="overline"
            sx={{ px: 1, color: "text.secondary", fontWeight: 700 }}
          >
            Account Profile
          </Typography>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent
              sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}
            >
              <PersonIcon color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {userInfo?.firstName} {userInfo?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Role: {userInfo?.userRole?.replace(/_/g, " ")}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* 2. BRANCH DEFAULTS */}
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              px: 1,
            }}
          >
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              Branch Configuration (Server)
            </Typography>
            {canUpdate && (
              <Stack direction="row" spacing={1}>
                <AppButton
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setEmailModalOpen(true)}
                >
                  Email Config
                </AppButton>
                <AppButton
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => setBranchModalOpen(true)}
                >
                  Edit Settings
                </AppButton>
              </Stack>
            )}
          </Stack>

          {loadingBranchSettings && !branchSettings ? (
            <AppCircularProgress />
          ) : (
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <List disablePadding>
                <ListItem>
                  <ListItemText
                    primary="Default Currency"
                    secondary={branchSettings?.defaultCurrencyCode || "Not Set"}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Timezone"
                    secondary={branchSettings?.timezone || "Not Set"}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Default Payment Method"
                    secondary={
                      branchSettings?.defaultPaymentMethodName || "Not Set"
                    }
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Payments Integration"
                    secondary={
                      branchSettings?.paymentIntegration?.active
                        ? "Active"
                        : "Disabled"
                    }
                    slotProps={{
                      secondary: {
                        color: branchSettings?.paymentIntegration?.active
                          ? "success"
                          : "text.secondary",
                      },
                    }}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Communications Integration"
                    secondary={
                      branchSettings?.communicationIntegration?.active
                        ? "Active"
                        : "Disabled"
                    }
                    slotProps={{
                      secondary: {
                        color: branchSettings?.communicationIntegration?.active
                          ? "success"
                          : "text.secondary",
                      },
                    }}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="Default Customer"
                    secondary={branchSettings?.defaultCustomerName || "Not Set"}
                  />
                </ListItem>
                <Divider />

                <ListItem>
                  <ListItemText
                    primary="eTIMS Integration"
                    secondary={
                      branchSettings?.etimsEnabled ? "Enabled" : "Disabled"
                    }
                    slotProps={{
                      secondary: {
                        color: branchSettings?.etimsEnabled
                          ? "success.main"
                          : "text.secondary",
                      },
                    }}
                  />
                </ListItem>
              </List>
            </Card>
          )}
        </Box>

        {/* 3. OPERATIONAL DEFAULTS */}
        <Box>
          <Typography
            variant="overline"
            sx={{ px: 1, color: "text.secondary", fontWeight: 700 }}
          >
            Local Operational Defaults
          </Typography>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <ListItem
              component="div"
              onClick={() => setDefaultsModalOpen(true)}
              sx={{
                py: 2,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <ListItemIcon>
                <SettingsSuggestIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography sx={{ fontWeight: "bold" }}>
                    Data Entry Defaults
                  </Typography>
                }
                secondary="Set default tax codes, units, and payment modes"
              />
              <ChevronRightIcon color="action" />
            </ListItem>
          </Card>
        </Box>

        {/* 6. ADVANCED DIAGNOSTICS */}
        <Box>
          <Typography
            variant="overline"
            sx={{ px: 1, color: "error.main", fontWeight: 700 }}
          >
            Advanced Diagnostics
          </Typography>
          <Stack
            divider={<Divider />}
            sx={{
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.05),
            }}
          >
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Clear Local Cache
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Wipes POS carts and logs you out immediately.
                </Typography>
              </Box>
              <AppButton
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={() => setClearCacheOpen(true)}
              >
                Clear & Logout
              </AppButton>
            </Box>

            {canUpdate && (
              <Box
                sx={{
                  p: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    Reset Local Defaults
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wipes saved printer names and entry defaults.
                  </Typography>
                </Box>
                <AppButton
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setResetDialogOpen(true)}
                >
                  Reset Defaults
                </AppButton>
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* --- MODALS --- */}
      {branchModalOpen && (
        <UpdateBranchSettingsModal
          open={branchModalOpen}
          onClose={() => setBranchModalOpen(false)}
          branchSettings={branchSettings}
          loading={loadingUpdateBranchSettings}
          error={errorUpdateBranchSettings}
        />
      )}

      {defaultsModalOpen && (
        <UserDefaultsModal
          open={defaultsModalOpen}
          onClose={() => setDefaultsModalOpen(false)}
        />
      )}

      <ConfirmDialog
        open={clearCacheOpen}
        onClose={() => setClearCacheOpen(false)}
        onConfirm={handleClearCacheConfirm}
        title="Clear App Cache?"
        description="This will clear your locally saved carts, session data, and sign you out immediately."
        confirmText="Yes, Clear Everything"
      />

      <AppEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        loading={loadingEmail}
        title="Email Configuration"
        description="Send the current branch configuration to an email address."
      />
    </PageContainer>
  );
};

export default SettingsPage;
